import {
  Component,
  ChangeDetectionStrategy,
  computed,
  input,
  output,
  inject,
  ElementRef,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { faShieldKeyhole, faArrowPointer, faXmark } from '@fortawesome/pro-duotone-svg-icons';
import { isBlinkBrowser, isFirefoxBrowser, isSafariBrowser } from '@4players/odin-common';

type BrowserType = 'chromium' | 'safari' | 'firefox';

@Component({
  selector: 'andor-permission-tutorial',
  standalone: true,
  imports: [TranslatePipe, FaDuotoneIconComponent],
  templateUrl: './permission-tutorial.html',
  styleUrl: './permission-tutorial.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    popover: 'manual',
  },
})
export class PermissionTutorial implements AfterViewInit, OnDestroy {
  cameraDenied = input(false);
  microphoneDenied = input(false);
  dismissed = output<void>();

  private _el = inject(ElementRef).nativeElement as HTMLElement;

  protected readonly faShieldKeyhole = faShieldKeyhole;
  protected readonly faArrowPointer = faArrowPointer;
  protected readonly faXmark = faXmark;

  browser = computed<BrowserType>(() => {
    if (isSafariBrowser()) return 'safari';
    if (isFirefoxBrowser()) return 'firefox';
    return 'chromium';
  });

  deviceLabel = computed(() => {
    const cam = this.cameraDenied();
    const mic = this.microphoneDenied();
    if (cam && mic) return 'camera and microphone';
    if (cam) return 'camera';
    return 'microphone';
  });

  continueLabel = computed(() => {
    const cam = this.cameraDenied();
    const mic = this.microphoneDenied();
    if (cam && mic) return 'Continue without camera & microphone';
    if (cam) return 'Continue without camera';
    return 'Continue without microphone';
  });

  steps = computed(() => {
    const browser = this.browser();
    const device = this.deviceLabel();

    const browserSteps: Record<
      BrowserType,
      { title: string; description: string; image: string }[]
    > = {
      chromium: [
        {
          title: 'Open site settings',
          description: `Click the icon left of the URL in your address bar to open the site permissions.`,
          image: 'assets/tutorials/chrome/chrome_1.png',
        },
        {
          title: `Allow ${device} access`,
          description: `Set the ${device} permission to "Allow", then reload the page.`,
          image: 'assets/tutorials/chrome/chrome_2.png',
        },
      ],
      safari: [
        {
          title: 'Open Safari Settings',
          description: `Click "Safari" in the menu bar and select "Settings…".`,
          image: 'assets/tutorials/safari/safari_1.png',
        },
        {
          title: 'Go to Websites',
          description: `Select the "Websites" tab at the top of the settings window.`,
          image: 'assets/tutorials/safari/safari_2.png',
        },
        {
          title: `Select ${device}`,
          description: `Find and click "${device}" in the left sidebar.`,
          image: 'assets/tutorials/safari/safari-3.png',
        },
        {
          title: 'Set to Allow',
          description: `Change the permission for this site to "Allow", then reload the page.`,
          image: 'assets/tutorials/safari/safari-4.png',
        },
      ],
      firefox: [
        {
          title: 'Open site permissions',
          description: `Click the shield or lock icon in the address bar to view site permissions.`,
          image: 'assets/tutorials/firefox/firefox_1.png',
        },
        {
          title: `Allow ${device} access`,
          description: `Remove the blocked permission for ${device}, then reload the page.`,
          image: 'assets/tutorials/firefox/firefox_2.png',
        },
      ],
    };

    return browserSteps[browser];
  });

  ngAfterViewInit() {
    this._el.showPopover();
  }

  ngOnDestroy() {
    try {
      this._el.hidePopover();
    } catch {}
  }

  dismiss() {
    this.dismissed.emit();
  }
}
